/**
 * Driver location tracking (Phase 17 Commit 2).
 *
 * Posts a fix every 30s while on shift via navigator.geolocation.watchPosition
 * (enableHighAccuracy). Pings made while offline (a basement, a dead zone --
 * this is a delivery driver, not a desk job) are queued in localStorage and
 * flushed together on the next successful post, rather than lost.
 *
 * This module owns exactly one watchPosition subscription for the whole
 * driver app -- mounted once in DriverShell so it keeps running across page
 * navigation, not restarted per-screen.
 */
import { api, ApiError } from './api';

const QUEUE_KEY = 'driver_location_queue_v1';
const POST_INTERVAL_MS = 30_000;

interface QueuedPing {
  coordinates: [number, number];
  accuracy_m?: number;
  speed_kmh?: number;
  heading?: number;
  recorded_at: string;
}

function readQueue(): QueuedPing[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
function writeQueue(pings: QueuedPing[]) {
  try {
    // Cap the queue -- a phone offline for days shouldn't accumulate an
    // unbounded backlog; keep the most recent stretch.
    localStorage.setItem(QUEUE_KEY, JSON.stringify(pings.slice(-500)));
  } catch {
    // localStorage full or unavailable -- pings are best-effort, not worth
    // crashing the app over.
  }
}
function enqueue(ping: QueuedPing) {
  writeQueue([...readQueue(), ping]);
}

function positionToPing(pos: GeolocationPosition): QueuedPing {
  return {
    coordinates: [pos.coords.longitude, pos.coords.latitude],
    accuracy_m: pos.coords.accuracy ?? undefined,
    speed_kmh: pos.coords.speed != null ? Math.max(0, pos.coords.speed) * 3.6 : undefined,
    heading: pos.coords.heading ?? undefined,
    recorded_at: new Date().toISOString(),
  };
}

async function flush() {
  const queued = readQueue();
  if (queued.length === 0) return;
  try {
    await api('/rider/location', { method: 'POST', body: { pings: queued.slice(0, 200) } });
    // Only clear what was actually sent -- more than 200 queued is rare but possible.
    writeQueue(readQueue().slice(200));
  } catch {
    // Still offline or the request failed -- leave the queue for next time.
  }
}

let watchId: number | null = null;
let flushTimer: number | null = null;
let latestPing: QueuedPing | null = null;

/**
 * Get a single fresh fix, for the "you need a location fix to go on shift"
 * gate. Does not start the ongoing watch.
 */
export function getOneFix(): Promise<QueuedPing> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('This browser does not support location'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(positionToPing(pos)),
      (err) => reject(new Error(err.message || 'Could not get your location')),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 },
    );
  });
}

/** Post one fix immediately (used right after getOneFix, before the ongoing watch starts). */
export async function postOneFix(ping: QueuedPing): Promise<void> {
  try {
    await api('/rider/location', { method: 'POST', body: ping });
  } catch (err) {
    if (err instanceof ApiError) throw err;
    enqueue(ping); // network-level failure (offline) -- queue it, don't lose it
  }
}

/**
 * Start the ongoing watch: navigator.geolocation.watchPosition keeps the
 * latest fix current; a 30s interval posts it (or queues it offline).
 * Idempotent -- calling this while already watching is a no-op.
 */
export function startWatching() {
  if (watchId != null) return;
  if (!('geolocation' in navigator)) return;

  watchId = navigator.geolocation.watchPosition(
    (pos) => { latestPing = positionToPing(pos); },
    () => { /* a transient GPS error shouldn't tear down the watch -- just skip this tick */ },
    { enableHighAccuracy: true, maximumAge: 20_000, timeout: 25_000 },
  );

  flushTimer = window.setInterval(async () => {
    await flush(); // send anything queued from a previous offline stretch first
    if (latestPing) {
      await postOneFix(latestPing);
      latestPing = null;
    }
  }, POST_INTERVAL_MS);
}

export function stopWatching() {
  if (watchId != null) {
    navigator.geolocation.clearWatch(watchId);
    watchId = null;
  }
  if (flushTimer != null) {
    window.clearInterval(flushTimer);
    flushTimer = null;
  }
  latestPing = null;
}

export function isWatching() {
  return watchId != null;
}
